// externals
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PencilSquareIcon } from '@interstate/components/Icons/PencilSquareIcon';
import { Interstitial as LoadingIndicator } from '@interstate/components/Interstitial';
import { Button } from '@interstate/components/Button';
import { PlusIcon } from '@interstate/components/Icons';
import { Box } from '@interstate/components/Box';
import { Switch } from '@interstate/components/Switch';
import { Tooltip } from '@interstate/components/Tooltip';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
// store
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as vehicleProtectionActionCreators from '@makemydeal/dr-dash-store/actions/vehicleProtectionActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';

// style
import {
    VehicleProtectionContent,
    LoaderWrapper,
    TableWrapper,
    VehicleOverrideEditingContainer,
    VehicleEditButtonContainer
} from './VehicleProtection.style';

// components
import VehicleProtectionTableCompact from '../vehicleProtectionTable/VehicleProtectionTableCompact';
import EditProductDrawer from '../editProductDrawer/EditProductDrawer';
import EditDealAttributesDrawer from '../editDealAttributesDrawer/EditDealAttributesDrawer';
import TableGlobalAlerts from '../vehicleProtectionTableGlobalAlerts/TableGlobalAlerts';
import ProductSummaryTable from '../productSummary/ProductSummaryTable';
import { useManualVehicleProtection } from '../manualVppTable/VehicleProtectionManualContext';
import { Typography } from '@interstate/components/Typography';

// types
import type { DealProductWithProductCode } from '@makemydeal/dr-dash-types';
import { GAP } from '@makemydeal/dr-shared-types';
import { DEFAULT_PAGE_SIZE } from './constants';
import { useVehicleProtectionContext } from '../../context/VehicleProtectionContext';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

const VehicleProtectionCompact = () => {
    const { isDarwinVPPEditDisabled, isDarwinVppEditGuardEnabled, allowOverrideEditing, setAllowOverrideEditing } =
        useVehicleProtectionContext();
    const editableProductCodeSelected = useSelector(vehicleProtectionSelectors.getEditableProductCode);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const [editableProductCode, setEditableProductCode] = useState<string | undefined>(
        isGCDynamicValidationsEnabled ? editableProductCodeSelected : undefined
    );
    const theme = useInterstateTheme();
    const [page, setPage] = useState(1);

    const { manualProducts, onManualProductAdd, onManualProductDelete } = useManualVehicleProtection();

    const catalogProducts = useSelector(vehicleProtectionSelectors.getCatalogVppProducts);
    const products = useMemo(() => {
        catalogProducts.sort((a, b) => {
            return (b.selected === true ? 1 : 0) - (a.selected === true ? 1 : 0);
        });
        return [...manualProducts, ...catalogProducts];
    }, [manualProducts, catalogProducts]);
    const summary = useSelector(vehicleProtectionSelectors.getVppSummary);
    const selectedProductsCount = useSelector(vehicleProtectionSelectors.getSelectedCatalogVppProductsCount);
    const isFetchingVppProducts = useSelector(vehicleProtectionSelectors.isFetchingVppProducts);
    const isFetchingVppRates = useSelector(vehicleProtectionSelectors.isFetchingVppRates);
    const productsEligibleForSelection = useSelector(vehicleProtectionSelectors.getVppProductsEligibleForSelection);
    const isFetchingFIE = isFetchingVppProducts || isFetchingVppRates;

    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();

    const [isDealAttributesDrawerOpen, setDealAttributesDrawerOpen] = useState(false);

    const onOpenDealAttributesDrawer = useCallback(() => setDealAttributesDrawerOpen(true), []);
    const onCloseDealAttributesDrawer = useCallback(() => setDealAttributesDrawerOpen(false), []);

    const onProductEdit = useCallback((product: DealProductWithProductCode) => {
        setEditableProductCode(product.productCode);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setEditableProductCode(undefined);
        if (isGCDynamicValidationsEnabled) {
            dispatch(vehicleProtectionActionCreators.resetEditableVppProductCode());
        }
    }, []);

    const onProductApply = useCallback((product: DealProductWithProductCode) => {
        if (product.selected) {
            dispatch(vehicleProtectionActionCreators.unselectVppProduct(product.productCode));

            if (isGCDynamicValidationsEnabled) {
                const errorState = {
                    streetAddress: false,
                    city: false,
                    zip: false,
                    state: false
                };
                dispatch(dealScreenErrorActionCreators.setErrorFields(GAP, errorState));
                // GAP errors are keyed per product, so re-run validation to drop the unselected one.
                dispatch(contractingActionCreators.refreshContractValidationErrors());
            }
        } else {
            dispatch(vehicleProtectionActionCreators.selectVppProduct(product.productCode));
        }
    }, []);

    const onProductApplyAll = useCallback(() => {
        if (selectedProductsCount === productsEligibleForSelection.length) {
            dispatch(vehicleProtectionActionCreators.unselectAllVppProducts());
        } else {
            dispatch(vehicleProtectionActionCreators.selectAllVppProducts());
        }
    }, [dispatch, selectedProductsCount, productsEligibleForSelection.length]);

    return (
        <>
            <TableGlobalAlerts />

            <Box sx={{ marginBottom: '1rem' }}>
                <VehicleOverrideEditingContainer>
                    <Box display="flex" alignItems="center" gap="0.5rem">
                        {isDarwinVppEditGuardEnabled && (
                            <>
                                <Typography variant="label-md" color="sem.color.on-surface.muted" tag="span">
                                    Allow Override Editing
                                </Typography>
                                <Box sx={{ zIndex: 2 }}>
                                    <Tooltip
                                        data-testid="allow-override-editing-tooltip"
                                        size="large"
                                        toolTipContent="Enabling 'Allow Override Editing' toggle will allow you to edit values."
                                        position="right"
                                    >
                                        <InformationCircleIcon
                                            data-testid="allow-override-editing-info-icon"
                                            color={theme.tokens.BaseColorBlue500}
                                        />
                                    </Tooltip>
                                </Box>
                                <Switch
                                    name="allow-override-editing"
                                    data-testid="allow-override-editing-toggle"
                                    checked={allowOverrideEditing}
                                    onClick={() => setAllowOverrideEditing(!allowOverrideEditing)}
                                />
                            </>
                        )}
                    </Box>

                    <VehicleEditButtonContainer>
                        {!isDarwinVPPEditDisabled && (
                            <>
                                <Button
                                    data-testid="add-manual-vpp-button"
                                    buttonStyle="tertiary"
                                    onClick={() => {
                                        onManualProductAdd();
                                        setPage(1);
                                    }}
                                    size="small"
                                    startIcon={<PlusIcon />}
                                    disabled={isLocked}
                                >
                                    Add Manual VPP
                                </Button>
                                <Button
                                    data-testid="edit-deal-attributes-link"
                                    buttonStyle="secondary"
                                    onClick={onOpenDealAttributesDrawer}
                                    size="small"
                                    startIcon={<PencilSquareIcon />}
                                    disabled={isLocked}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                    </VehicleEditButtonContainer>
                </VehicleOverrideEditingContainer>
            </Box>

            <VehicleProtectionContent $showPagination={products.length > DEFAULT_PAGE_SIZE}>
                {isFetchingFIE && !products.length ? (
                    <LoaderWrapper>
                        <LoadingIndicator size="small" />
                    </LoaderWrapper>
                ) : (
                    <>
                        <TableWrapper>
                            <VehicleProtectionTableCompact
                                onProductApply={onProductApply}
                                onProductApplyAll={onProductApplyAll}
                                onProductEdit={onProductEdit}
                                products={products}
                                currentPage={page}
                                setCurrentPage={setPage}
                                id="vehicle-protection-plan-table"
                            />
                        </TableWrapper>
                        <ProductSummaryTable
                            summary={summary}
                            products={products}
                            onCatalogProductRemove={onProductApply}
                            onManualProductRemove={onManualProductDelete}
                        />
                    </>
                )}
                <EditProductDrawer productCode={editableProductCode} onClose={handleDrawerClose} />
                <EditDealAttributesDrawer show={isDealAttributesDrawerOpen} onClose={onCloseDealAttributesDrawer} />
            </VehicleProtectionContent>
        </>
    );
};

export default VehicleProtectionCompact;
